import React, { useRef, useCallback, useMemo } from "react";
import { NavigationProvider } from "../../context/NavigationContext";
import * as Styles from "./HealthAnalyseParentWrapper.styles";
import LogoSrc from "assets/images/Foodiary-Logo.png";
import Steps from "rc-steps";
import "rc-steps/assets/index.css";
import "./custom-steps.css";
import HealthAnalyseHeader from "./HealthAnalyseHeader";

const HealthAnalyseParentWrapper = ({
  children,
  currentPath,
  progressStep,
  title = "",
  subTitle = "",
  btnText = "",
  noBackButton,
}) => {
  const componentName = children.type.displayName || children.type.name;
  const planRouteExist =
    currentPath.includes("/plan") || currentPath.includes("/welcome")
      ? true
      : false;

  // Use useRef instead of useState to prevent re-renders
  const navigationMethodsRef = useRef({
    goBack: null,
    handleNextPage: null,
  });

  // Register callback that updates the ref directly
  const registerMethods = useCallback((methods) => {
    console.log("Navigation methods registered:", methods);
    if (methods?.goBack && methods?.handleNextPage) {
      navigationMethodsRef.current = methods;
    }
  }, []);

  // Context value with memoization
  const navigationContextValue = useMemo(
    () => ({
      registerMethods,
    }),
    [registerMethods]
  );

  // Handler functions that use the ref
  const handleBack = (e) => {
    e && e.preventDefault();
    if (navigationMethodsRef.current?.goBack) {
      navigationMethodsRef.current.goBack();
    }
  };

  const handleNext = () => {
    if (navigationMethodsRef.current?.handleNextPage) {
      // Scroll to top before navigating to next page
      // window.scrollTo(0, 0);
      navigationMethodsRef.current.handleNextPage();
    }
  };

  return (
    <NavigationProvider value={navigationContextValue}>
      {/* Mobile view */}
      <Styles.MobileView>
        <Styles.HealthAnalyseContainer>
          <Styles.HeaderCard>
            <Styles.LogoWrapper>
              <img src={LogoSrc} alt="foodiary logo" />
            </Styles.LogoWrapper>
            <Styles.StepsCenter>
              <Styles.StepsContainer>
                <Steps
                  current={progressStep}
                  direction="horizontal"
                  labelPlacement="vertical"
                  className="foodiary-steps"
                  initial={0}
                  items={mobileSteps.map((step, index) => ({
                    key: step.key,
                    title: step.title,
                    status:
                      index === progressStep
                        ? "process"
                        : index < progressStep
                        ? "finish"
                        : "wait",
                  }))}
                />
              </Styles.StepsContainer>
            </Styles.StepsCenter>
          </Styles.HeaderCard>

          <Styles.ContentSection>
            {!planRouteExist && (
              <HealthAnalyseHeader
                headline={title}
                subHeadline={subTitle}
                handleNextPage={handleNext}
                btnText={btnText}
                handleBack={handleBack}
                noBackButton={noBackButton}
              />
            )}

            {/* Clone the children and pass down the current path and progress step */}
            {React.isValidElement(children)
              ? React.cloneElement(children, { currentPath, progressStep })
              : children}
          </Styles.ContentSection>
          {!planRouteExist && (
            <button
              onClick={handleNext}
              className="btn-dark w-full mt-8 btn-show"
            >
              {btnText}
            </button>
          )}
        </Styles.HealthAnalyseContainer>
      </Styles.MobileView>

      {/* Desktop view */}
      <Styles.DesktopView>
        <Styles.DesktopLayout
          fullHeight={
            componentName == "Teilnehmer" ||
            componentName == "App" ||
            componentName == "Benefits"
          }
        >
          {/* Sidebar with vertical steps */}
          <Styles.DesktopSidebar>
            <Styles.DesktopLogoWrapper>
              <img src={LogoSrc} alt="foodiary logo" />
            </Styles.DesktopLogoWrapper>

            <Styles.WelcomeText>
              Herzlich Willkommen zum Erstgespräch.
            </Styles.WelcomeText>

            <Styles.StepsLabel>Schritte</Styles.StepsLabel>

            <Styles.VerticalStepsList>
              {desktopSteps.map((step, index) => (
                <Styles.VerticalStepItem key={step.key}>
                  <Styles.StepCircle
                    active={index <= progressStep}
                    lastChild={index == desktopSteps.length - 1}
                  />
                  <Styles.StepContent>
                    <Styles.StepTitle active={index <= progressStep}>
                      {step.title}
                    </Styles.StepTitle>
                    <Styles.StepDescription>
                      {step.description}
                    </Styles.StepDescription>
                  </Styles.StepContent>
                  {index < desktopSteps.length - 1 && (
                    <Styles.VerticalLine active={index <= progressStep} />
                  )}
                </Styles.VerticalStepItem>
              ))}
            </Styles.VerticalStepsList>
          </Styles.DesktopSidebar>

          {/* Main content */}
          <Styles.DesktopContent>
            {!planRouteExist && (
              <HealthAnalyseHeader
                headline={title}
                subHeadline={subTitle}
                handleNextPage={handleNext}
                btnText={btnText}
                handleBack={handleBack}
                noBackButton={noBackButton}
              />
            )}

            {React.isValidElement(children)
              ? React.cloneElement(children, { currentPath, progressStep })
              : children}
          </Styles.DesktopContent>
        </Styles.DesktopLayout>
      </Styles.DesktopView>
    </NavigationProvider>
  );
};

export default HealthAnalyseParentWrapper;

const mobileSteps = [
  { key: "anamnese", title: "Anamnese" },
  { key: "auswertung", title: "Auswertung" },
  { key: "start", title: "Start" },
];

// Desktop vertical steps
const desktopSteps = [
  {
    key: "anamnese",
    title: "Anamnese",
    description: "Wir erstellen deinen Ernährungsplan",
  },
  {
    key: "auswertung",
    title: "Auswertung",
    description: "Ich präsentiere dir deinen Ernährungsplan",
  },
  {
    key: "start",
    title: "Start",
    description: "Wir erreichen dein Ziel gemeinsam",
  },
  // {
  //   key: "tipps",
  //   title: "Tipps & Tricks",
  //   description: "Wichtige Grundlagen für eine gesunde Ernährung.",
  // },
  // {
  //   key: "start",
  //   title: "Start",
  //   description: "Du Entscheidest, ob wir gemeinsam starten.",
  // },
];
